import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
} from "@mui/material";
import { CheckRounded, GroupWorkRounded, SettingsRounded } from "@mui/icons-material";
import { corporate_getCorporateGroups } from "../../../network/api/corporate.api";
import { useSelector } from "react-redux";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { corporatePersonnel_update } from "../../../network/api/corporatePersonnel.api";
import { useNavigate } from "react-router-dom";

const _UserGroup = ({ corporatePersonnel, updatePage }) => {
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [groups, setGroups] = useState(null);
    const [updating, setUpdating] = useState(false);

    // استخراج لیست شناسه‌های گروه‌های کارمند (پشتیبانی از PersonnelGroups چندگانه یا PersonnelGroup تکی)
    const initialGroupIds = useMemo(() => {
        if (corporatePersonnel?.PersonnelGroups && Array.isArray(corporatePersonnel.PersonnelGroups)) {
            return corporatePersonnel.PersonnelGroups.map(g => g.Id);
        }
        if (corporatePersonnel?.PersonnelGroup?.Id) {
            return [corporatePersonnel.PersonnelGroup.Id];
        }
        return [];
    }, [corporatePersonnel]);

    const [selectedGroupIds, setSelectedGroupIds] = useState(initialGroupIds);

    useEffect(() => {
        getCorporateCategories();
    }, []);

    useEffect(() => {
        setSelectedGroupIds(initialGroupIds);
    }, [initialGroupIds]);

    function getCorporateCategories() {
        if (!corporate?.Id) return;
        corporate_getCorporateGroups({ Id: corporate.Id })
            .then(result => {
                setGroups(result.data.Data || []);
            })
            .catch(e => {
                setGroups([]);
                try {
                    error.showError({ message: e.response?.data?.Message || "خطا در دریافت لیست گروه‌ها" });
                } catch (f) {
                    error.showError({ message: "خطای نامشخص" });
                }
            });
    }

    const hasChanges = useMemo(() => {
        if (selectedGroupIds.length !== initialGroupIds.length) return true;
        const setA = new Set(selectedGroupIds);
        return initialGroupIds.some(id => !setA.has(id));
    }, [selectedGroupIds, initialGroupIds]);

    function handleSaveGroups() {
        setUpdating(true);
        corporatePersonnel_update({
            id: corporatePersonnel.Id,
            GroupIds: selectedGroupIds, // ارسال آرایه گروه‌های چندگانه طبق مستند جدید
            PersonelGroup: selectedGroupIds.length > 0 ? { Id: selectedGroupIds[0] } : null // حفظ سازگاری
        })
            .then(() => {
                setUpdating(false);
                error.showError({ message: "گروه‌های کارمند با موفقیت به‌روزرسانی شد" });
                if (updatePage) updatePage();
            })
            .catch(e => {
                setUpdating(false);
                try {
                    error.showError({ message: e.response?.data?.Message || "خطا در به‌روزرسانی گروه‌ها" });
                } catch (f) {
                    error.showError({ message: "خطای نامشخص" });
                }
            });
    }

    const isLoading = groups === null;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    p: 2,
                    px: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <GroupWorkRounded sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        گروه‌های سازمانی پرسنل
                    </Typography>
                </Box>
                {updating && <CircularProgress size={18} sx={{ color: 'primary.main' }} />}
            </Box>

            <CardContent sx={{ p: 2.5 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : groups?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="user-groups-select-label">انتخاب گروه‌ها (عضویت چندگانه)</InputLabel>
                            <Select
                                labelId="user-groups-select-label"
                                multiple
                                value={selectedGroupIds}
                                onChange={(e) => setSelectedGroupIds(e.target.value)}
                                disabled={updating}
                                input={<OutlinedInput label="انتخاب گروه‌ها (عضویت چندگانه)" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                بدون گروه (پیش‌فرض)
                                            </Typography>
                                        ) : (
                                            selected.map((gId) => {
                                                const grp = groups.find(g => g.Id === gId);
                                                return (
                                                    <Chip
                                                        key={gId}
                                                        label={grp?.Name || gId}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ fontWeight: 700 }}
                                                    />
                                                );
                                            })
                                        )}
                                    </Box>
                                )}
                                sx={{ borderRadius: 2.5 }}
                            >
                                {groups.map((group) => (
                                    <MenuItem key={group.Id} value={group.Id}>
                                        <Checkbox checked={selectedGroupIds.includes(group.Id)} size="small" />
                                        <Typography variant="body2">{group.Name}</Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {selectedGroupIds.length === 0
                                    ? "پرسنل می‌تواند همزمان عضو چند گروه سازمانی باشد (یا بدون گروه)."
                                    : `${selectedGroupIds.length} گروه سازمانی برای این کارمند انتخاب شده است.`}
                            </FormHelperText>
                        </FormControl>

                        {hasChanges && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    disabled={updating}
                                    onClick={() => setSelectedGroupIds(initialGroupIds)}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    انصراف
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    disabled={updating}
                                    onClick={handleSaveGroups}
                                    startIcon={<CheckRounded />}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    ذخیره تغییرات گروه
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            هنوز گروهی برای سازمان تعریف نشده است.
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SettingsRounded sx={{ ml: 0.8 }} />}
                            onClick={() => navigate("/personnel/groups")}
                            sx={{ borderRadius: 2.2, fontWeight: 700 }}
                        >
                            مدیریت و تعریف گروه‌ها
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default _UserGroup;
